import React, { useEffect } from "react";
import {
  Form,
  Input,
  Label,
  FormFeedback,
  Button,
  Row,
  Col,
  Spinner,
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import toast from "react-hot-toast";
import MasterCostCenterService from "../../../services/OrderingServices/MasterCostCenter";

// ─── Enum mappings ────────────────────────────────────────────────────────────
const CostCenterTypeEnum = {
  
  BillingCounter: 1, // API may return either label
  Kitchen: 2,
  Bar: 3,
};

const PrinterTypeEnum = {
  Local: 1,
  Network: 2,
  Dummy: 3,
};

const costCenterTypeOptions = [
  { value: 1, label: "Billing Counter" },
  { value: 2, label: "Kitchen" },
  { value: 3, label: "Bar" },
];

const printerTypeOptions = [
  { value: 1, label: "Local" },
  { value: 2, label: "Network" },
  { value: 3, label: "Dummy" },
];

// ─── Validation ───────────────────────────────────────────────────────────────
const validationSchema = Yup.object({
  code: Yup.string().required("Code is required"),
  name: Yup.string().required("Name is required"),
  description: Yup.string().nullable(),
  outletCode: Yup.string().required("Outlet code is required"),
  costCenterType: Yup.number()
    .nullable()
    .required("Cost center type is required"),
  printerType: Yup.number().nullable().required("Printer type is required"),
  printerName: Yup.string().nullable(),
  printerIpAddress: Yup.string().nullable(),
  printerPort: Yup.string().nullable(),
});

// ─── Component ────────────────────────────────────────────────────────────────
const MasterCostCenterForm = ({
  modal,
  toggleModal,
  selectedItem,
  fetchMasterCostCenter,
  outlet,
}) => {
  const isEdit = !!selectedItem?.id;

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: selectedItem?.id || 0,
      code: selectedItem?.code || "",
      name: selectedItem?.name || "",
      description: selectedItem?.description || "",
      outletCode: selectedItem?.outletCode || "",
      costCenterType: selectedItem?.costCenterType
        ? CostCenterTypeEnum[selectedItem.costCenterType] ?? null
        : null,
      printerType: selectedItem?.printerType
        ? PrinterTypeEnum[selectedItem.printerType] ?? null
        : null,
      printerName: selectedItem?.printerName || "",
      printerIpAddress: selectedItem?.printerIpAddress || "",
      printerPort: selectedItem?.printerPort || "",
      isActive: selectedItem?.isActive ?? true,
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      const payload = {
        code: values.code,
        name: values.name,
        description: values.description || null,
        outletCode: values.outletCode,
        costCenterType: values.costCenterType,
        printerType: values.printerType,
        printerName: values.printerName || null,
        printerIpAddress: values.printerIpAddress || null,
        printerPort: values.printerPort || null,
        isActive: values.isActive,
        ...(isEdit && { id: values.id }),
      };

      try {
        if (isEdit) {
          await MasterCostCenterService.update(values.id, payload);
          toast.success("Master cost center updated successfully.");
        } else {
          await MasterCostCenterService.create(payload);
          toast.success("Master cost center created successfully.");
          resetForm();
        }
        fetchMasterCostCenter();
        toggleModal();
      } catch (error) {
        console.error(error);
        toast.error("Error while submitting form.");
      }
    },
  });

  useEffect(() => {
    if (!modal) formik.resetForm();
  }, [modal]);

  const selectedCostCenterType = costCenterTypeOptions.find(
    (o) => o.value === formik.values.costCenterType
  );

  const selectedPrinterType = printerTypeOptions.find(
    (o) => o.value === formik.values.printerType
  );

  const outletOptions = outlet?.map((item) => ({
    value: item.code,
    label: `${item.name} (${item.code})`,
  }));

  const selectedOutlet = outletOptions?.find(
    (o) => o.value === formik.values.outletCode
  );

  return (
    <Offcanvas
      isOpen={modal}
      toggle={toggleModal}
      backdropClassName="bg-dark bg-opacity-50"
      direction="end"
      className="shadow-lg bg-white"
      style={{ width: "420px" }}
    >
      <OffcanvasHeader toggle={toggleModal}>
        {isEdit ? "Update" : "Add"} Master Cost Center
      </OffcanvasHeader>

      <OffcanvasBody style={{ overflowY: "auto", paddingBottom: "1rem" }}>
        <Form id="master-cost-center-form" onSubmit={formik.handleSubmit}>
          <Row>
            {/* Code */}
            <Col md={12} className="mb-3">
              <div className="form-floating">
                <Input
                  id="code"
                  name="code"
                  type="text"
                  placeholder="Enter code"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.code}
                  invalid={!!(formik.touched.code && formik.errors.code)}
                />
                <Label for="code">Code</Label>
                {formik.touched.code && formik.errors.code && (
                  <FormFeedback>{formik.errors.code}</FormFeedback>
                )}
              </div>
            </Col>

            {/* Name */}
            <Col md={12} className="mb-3">
              <div className="form-floating">
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="Enter name"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.name}
                  invalid={!!(formik.touched.name && formik.errors.name)}
                />
                <Label for="name">Name</Label>
                {formik.touched.name && formik.errors.name && (
                  <FormFeedback>{formik.errors.name}</FormFeedback>
                )}
              </div>
            </Col>

            {/* Description */}
            <Col md={12} className="mb-3">
              <div className="form-floating">
                <Input
                  id="description"
                  name="description"
                  type="text"
                  placeholder="Enter description"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.description}
                />
                <Label for="description">Description</Label>
              </div>
            </Col>

            {/* Outlet Code */}
            <Col md={12} className="mb-3">
              <Label>Outlet</Label>
              <Select
                id="outletCode"
                name="outletCode"
                options={outletOptions}
                value={selectedOutlet || null}
                onChange={(opt) =>
                  formik.setFieldValue("outletCode", opt ? opt.value : "")
                }
                onBlur={() => formik.setFieldTouched("outletCode", true)}
                placeholder="Select Outlet"
                isClearable
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  control: (base) => ({ ...base, padding: "0.2rem" }),
                }}
              />
              {formik.touched.outletCode && formik.errors.outletCode && (
                <div className="text-danger small mt-1">
                  {formik.errors.outletCode}
                </div>
              )}
            </Col>

            {/* Cost Center Type */}
            <Col md={12} className="mb-3">
              <Label>Cost Center Type</Label>
              <Select
                id="costCenterType"
                name="costCenterType"
                options={costCenterTypeOptions}
                value={selectedCostCenterType || null}
                onChange={(opt) =>
                  formik.setFieldValue("costCenterType", opt ? opt.value : null)
                }
                onBlur={() => formik.setFieldTouched("costCenterType", true)}
                placeholder="Select Cost Center Type"
                isClearable
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  control: (base) => ({ ...base, padding: "0.2rem" }),
                }}
              />
              {formik.touched.costCenterType && formik.errors.costCenterType && (
                <div className="text-danger small mt-1">
                  {formik.errors.costCenterType}
                </div>
              )}
            </Col>

            {/* Printer Type */}
            <Col md={12} className="mb-3">
              <Label>Printer Type</Label>
              <Select
                id="printerType"
                name="printerType"
                options={printerTypeOptions}
                value={selectedPrinterType || null}
                onChange={(opt) =>
                  formik.setFieldValue("printerType", opt ? opt.value : null)
                }
                onBlur={() => formik.setFieldTouched("printerType", true)}
                placeholder="Select Printer Type"
                isClearable
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  control: (base) => ({ ...base, padding: "0.2rem" }),
                }}
              />
              {formik.touched.printerType && formik.errors.printerType && (
                <div className="text-danger small mt-1">
                  {formik.errors.printerType}
                </div>
              )}
            </Col>

            {/* Printer Name */}
            <Col md={12} className="mb-3">
              <div className="form-floating">
                <Input
                  id="printerName"
                  name="printerName"
                  type="text"
                  placeholder="Enter printer name"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.printerName}
                />
                <Label for="printerName">Printer Name</Label>
              </div>
            </Col>

            {/* Printer IP Address */}
            <Col md={12} className="mb-3">
              <div className="form-floating">
                <Input
                  id="printerIpAddress"
                  name="printerIpAddress"
                  type="text"
                  placeholder="Enter IP address"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.printerIpAddress}
                />
                <Label for="printerIpAddress">Printer IP Address</Label>
              </div>
            </Col>

            {/* Printer Port */}
            <Col md={12} className="mb-3">
              <div className="form-floating">
                <Input
                  id="printerPort"
                  name="printerPort"
                  type="text"
                  placeholder="Enter printer port"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.printerPort}
                />
                <Label for="printerPort">Printer Port</Label>
              </div>
            </Col>

            {/* Is Active */}
            <Col md={12} className="mb-3">
              <div className="form-check ms-2">
                <Input
                  className="form-check-input"
                  type="checkbox"
                  id="isActive"
                  name="isActive"
                  checked={formik.values.isActive}
                  onChange={formik.handleChange}
                />
                <Label className="form-check-label" for="isActive">
                  Active
                </Label>
              </div>
            </Col>
          </Row>
        </Form>
      </OffcanvasBody>

      {/* ── Pinned footer ── */}
      <div
        className="d-flex gap-2 p-3 border-top bg-white"
        style={{ flexShrink: 0 }}
      >
        <button
          type="button"
          className="btn btn-danger w-100"
          onClick={toggleModal}
        >
          Cancel
        </button>
        <Button
          color="success"
          type="submit"
          form="master-cost-center-form"
          disabled={formik.isSubmitting}
          className="w-100"
        >
          {formik.isSubmitting && <Spinner size="sm" className="me-2" />}
          {formik.isSubmitting
            ? isEdit
              ? "Updating..."
              : "Creating..."
            : isEdit
            ? "Update"
            : "Create"}
        </Button>
      </div>
    </Offcanvas>
  );
};

export default MasterCostCenterForm;
